import { useState } from 'react';
import { Button, Modal } from 'equasion-lib';
import { retailCodeGtin14 } from '@/lib/scanning/retailCode';

const click = (onClick: () => void) => ({ onClick });

/** What a typed entry hands the count screen: shaped like a scan, flagged as typed. */
export type ZoneCountTypedEntry = {
  serialNumber: string | null;
  gtin: string | null;
  code: string | null;
  text: string;
  isManualEntry: true;
};

type ZoneCountManualEntryProps = {
  mode: 'add' | 'remove';
  zoneName: string;
  onSubmit: (entry: ZoneCountTypedEntry) => void;
  onClose: () => void;
};

/**
 * Manual entry for a pack whose barcode will not read. With a serial it counts (or, in Remove mode,
 * un-counts) that unit — the product code tells two products' identical serials apart; without one
 * it counts the product by number, exactly as scanning its barcode would. Stays open for the next
 * entry: the serial clears, the product code stays.
 */
function ZoneCountManualEntry({ mode, zoneName, onSubmit, onClose }: ZoneCountManualEntryProps) {
  const [code, setCode] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const ready = !!code.trim() || !!serialNumber.trim();

  const submit = () => {
    if (!ready) return;
    const typedCode = code.trim() || null;
    const gtin14 = typedCode ? retailCodeGtin14(typedCode.replace(/\D/g, '')) : null;
    const serial = serialNumber.trim() || null;
    onSubmit({
      serialNumber: serial,
      gtin: gtin14,
      code: typedCode,
      text: typedCode || serial || '',
      isManualEntry: true
    });
    setSent(serial ? `Serial ${serial} entered.` : `${typedCode} entered.`);
    setSerialNumber('');
  };

  return (
    <Modal
      className="zc-modal"
      modalOpen
      setModalOpen={(nextOpen?: boolean) => {
        if (!nextOpen) onClose();
      }}
    >
      <form
        className="zc-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <strong className="zc-form__title">
          {mode === 'remove' ? 'Take a unit off the count' : 'Type what is on the pack'}
        </strong>
        <p className="zc-form__text">
          For a barcode that will not scan in <strong>{zoneName}</strong>. Enter the serial number
          to count one serialized unit, or just the product code to count one unit of a product
          counted by number.
        </p>
        <label className="zc-field">
          <span>NDC / UPC / GTIN</span>
          <input
            name="code"
            autoFocus
            autoComplete="off"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        <label className="zc-field">
          <span>Serial number (serialized packs)</span>
          <input
            name="serialNumber"
            autoComplete="off"
            autoCapitalize="characters"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
          />
        </label>
        {sent && <p className="zc-form__sent">{sent}</p>}
        <Button type="primary" fullWidth isSubmit disabled={!ready}>
          {mode === 'remove' ? 'Take off' : 'Count it'}
        </Button>
        <Button type="secondary" fullWidth {...click(onClose)}>
          Done
        </Button>
      </form>
    </Modal>
  );
}

export default ZoneCountManualEntry;
